import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { translateBatch } from "../lib/api";
import { LANGUAGES } from "../mock/data";

const TranslateContext = createContext(null);

export const useTranslate = () => useContext(TranslateContext);

export const TranslateProvider = ({ children }) => {
  const [lang, setLang] = useState(LANGUAGES[0]); // English default
  const [translations, setTranslations] = useState({}); // { originalText: translatedText }
  const [loading, setLoading] = useState(false);
  const registry = useRef(new Set());

  const register = useCallback((text) => {
    if (text && typeof text === "string") registry.current.add(text);
  }, []);

  const runTranslation = useCallback(async (target) => {
    if (target.code === "en") {
      setTranslations({});
      return;
    }
    const texts = Array.from(registry.current);
    if (texts.length === 0) return;
    setLoading(true);
    try {
      // chunk to keep payloads reasonable
      const chunks = [];
      for (let i = 0; i < texts.length; i += 40) chunks.push(texts.slice(i, i + 40));
      const results = await Promise.all(
        chunks.map((c) => translateBatch(c, target.name, target.code))
      );
      const merged = Object.assign({}, ...results);
      setTranslations(merged);
    } catch (e) {
      console.error("translation failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeLanguage = useCallback((target) => {
    setLang(target);
    document.documentElement.dir = target.rtl ? "rtl" : "ltr";
    document.documentElement.lang = target.code;
    const LATIN = ["en", "es", "fr", "de", "pt", "it", "tr", "nl", "id", "pl", "sw", "vi"];
    document.documentElement.setAttribute("data-script", LATIN.includes(target.code) ? "latin" : "nonlatin");
    runTranslation(target);
  }, [runTranslation]);

  const t = useCallback((text) => {
    register(text);
    if (lang.code === "en") return text;
    return translations[text] || text;
  }, [lang, translations, register]);

  return (
    <TranslateContext.Provider value={{ lang, changeLanguage, t, register, loading, translations }}>
      {children}
    </TranslateContext.Provider>
  );
};

// Reusable translatable text node
export const T = ({ children, as: Tag = "span", className, ...rest }) => {
  const ctx = useTranslate();
  const text = typeof children === "string" ? children : "";
  useEffect(() => { ctx?.register(text); }, [text, ctx]);
  const out = ctx ? ctx.t(text) : text;
  return <Tag className={className} {...rest}>{out}</Tag>;
};
